import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, TextField, createFilterOptions } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import CloseIcon from "@mui/icons-material/Close";

type OptionType = {
  id?: string | number;
  value?: string;
  text?: string;
};

type InternalOption = {
  id?: string | number;
  value: string;
  text: string;
  inputValue?: string;
  isCreateOption?: boolean;
};

interface CreatableSingleDropDownProps {
  inputLabel?: string;
  value: OptionType | string | null;
  name?: string;
  list?: OptionType[] | string[];
  inputHelp?: string;
  placeholder?: string;
  required?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  onChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => void;
  sx?: object;
}

const toInternalOption = (option: OptionType | string): InternalOption => {
  if (typeof option === "string") {
    return { value: option, text: option };
  }

  const resolved =
    option.value ?? option.text ?? (option.id !== undefined ? String(option.id) : "");

  return {
    id: option.id,
    value: resolved,
    text: option.text ?? option.value ?? resolved,
  };
};

const resolveValue = (option: OptionType | string | null): string => {
  if (!option) return "";
  if (typeof option === "string") return option;
  if (option.value) return option.value;
  if (option.text) return option.text;
  if (option.id !== undefined && option.id !== null) return String(option.id);
  return "";
};

const filter = createFilterOptions<InternalOption>();

const normalizeOptionValue = (value: string): string => value.trim().toLowerCase();

function CreatableSingleDropDown({
  inputLabel,
  value,
  name,
  list = [],
  inputHelp,
  placeholder,
  required,
  size = "small",
  disabled,
  onChange,
  sx,
}: Readonly<CreatableSingleDropDownProps>) {
  const [options, setOptions] = useState<InternalOption[]>(list.map(toInternalOption));
  const [inputValue, setInputValue] = useState("");
  const [currentSelectionValue, setCurrentSelectionValue] = useState(() => resolveValue(value));
  const [createdOptionValues, setCreatedOptionValues] = useState<Set<string>>(new Set());
  const [showAllOnOpen, setShowAllOnOpen] = useState(false);
  const fontSize = "0.75rem";
  const fontFamily = "inherit";
  const defaultBorderColor = "rgba(0, 0, 0, 0.23)";
  const hoverBorderColor = "rgba(0, 0, 0, 0.87)";
  const defaultSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: "#fff",
      fontSize,
      fontFamily,
      minHeight: 30,
      height: 30,
      boxSizing: "border-box",
      paddingTop: "4px",
      paddingBottom: "4px",
      paddingRight: "36px",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.03)",
      borderRadius: 1,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: defaultBorderColor,
        borderWidth: 1,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hoverBorderColor,
      },
      "&:hover": {
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#ff6d00",
        borderWidth: 1,
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(255, 109, 0, 0.12)",
      },
    },
    "& .MuiAutocomplete-input": {
      fontSize,
      fontFamily,
      padding: "0 !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
    },
  };

  useEffect(() => {
    const baseOptions = list.map(toInternalOption);
    const baseOptionValues = new Set(
      baseOptions.map((option) => normalizeOptionValue(option.value))
    );

    setOptions((prev) => {
      const createdOptions = prev.filter((option) => {
        const normalized = normalizeOptionValue(option.value);
        return createdOptionValues.has(normalized) && !baseOptionValues.has(normalized);
      });

      return [...baseOptions, ...createdOptions];
    });

    // Drop "created" markers once the option is provided by the parent list.
    setCreatedOptionValues((prev) => {
      const filtered = Array.from(prev).filter(
        (value) => !baseOptionValues.has(value)
      );
      return filtered.length === prev.size ? prev : new Set(filtered);
    });
  }, [list, createdOptionValues]);

  useEffect(() => {
    setInputValue(resolveValue(value));
    setCurrentSelectionValue(resolveValue(value));
  }, [value]);

  const selectedOption = useMemo(() => {
    const resolvedValue = resolveValue(value).trim();
    if (!resolvedValue) return null;

    const matched = options.find(
      (option) =>
        option.value.toLowerCase() === resolvedValue.toLowerCase() ||
        option.text.toLowerCase() === resolvedValue.toLowerCase()
    );

    return matched ?? { value: resolvedValue, text: resolvedValue };
  }, [options, value]);

  const createSyntheticEvent = (
    fieldName: string,
    fieldValue: string
  ): React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent => {
    return {
      target: {
        name: fieldName,
        value: fieldValue,
      },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent;
  };

  const removeCustomOption = (option: InternalOption) => {
    const normalizedOptionValue = normalizeOptionValue(option.value);

    setOptions((prev) =>
      prev.filter((item) => item.value.trim().toLowerCase() !== normalizedOptionValue)
    );
    setCreatedOptionValues((prev) => {
      const next = new Set(prev);
      next.delete(normalizedOptionValue);
      return next;
    });

    const selectedValue = normalizeOptionValue(resolveValue(value));
    const displayedInputValue = normalizeOptionValue(inputValue);
    if (
      selectedValue === normalizedOptionValue ||
      displayedInputValue === normalizedOptionValue
    ) {
      setInputValue("");
      setCurrentSelectionValue("");
      onChange(createSyntheticEvent(name ?? "", ""));
    }
  };

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      fullWidth
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      disabled={disabled}
      size={size}
      data-testid={name}
      onOpen={() => {
        const inputMatchesSelected =
          normalizeOptionValue(inputValue) ===
          normalizeOptionValue(currentSelectionValue);
        setShowAllOnOpen(inputMatchesSelected);
      }}
      onClose={() => setShowAllOnOpen(false)}
      onInputChange={(event, newInputValue) => {
        setShowAllOnOpen(false);
        setInputValue(newInputValue);
      }}
      filterOptions={(currentOptions, params) => {
        const shouldShowAllOptions =
          showAllOnOpen &&
          normalizeOptionValue(params.inputValue) ===
            normalizeOptionValue(currentSelectionValue);

        const effectiveParams = shouldShowAllOptions
          ? { ...params, inputValue: "" }
          : params;

        const filtered = filter(currentOptions, effectiveParams);
        const inputValue = effectiveParams.inputValue.trim();

        const exists = currentOptions.some(
          (option) =>
            normalizeOptionValue(option.text) === normalizeOptionValue(inputValue)
        );

        if (inputValue !== "" && !exists) {
          filtered.push({
            value: inputValue,
            text: `Create "${inputValue}"`,
            inputValue,
            isCreateOption: true,
          });
        }

        return filtered;
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if (option.inputValue) return option.text;
        return option.text;
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const normalizedOptionValue = normalizeOptionValue(option.value);
        const isRemovableCustomOption =
          !option.isCreateOption && createdOptionValues.has(normalizedOptionValue);

        return (
          <li key={key} {...optionProps} style={{ fontSize: "0.75rem" }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{option.text}</span>
              {isRemovableCustomOption && (
                <IconButton
                  size="small"
                  aria-label={`Remove option ${option.text}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeCustomOption(option);
                  }}
                  sx={{ ml: 1, p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          </li>
        );
      }}
      onChange={(event, newValue) => {
        let resolvedValue = "";

        if (!newValue) {
          resolvedValue = "";
        } else if (typeof newValue === "string") {
          resolvedValue = newValue;
        } else if (newValue.inputValue) {
          resolvedValue = newValue.inputValue;
          setOptions((prev) => {
            const exists = prev.some(
              (option) =>
                normalizeOptionValue(option.value) ===
                normalizeOptionValue(resolvedValue)
            );
            if (exists) return prev;
            return [...prev, { value: resolvedValue, text: resolvedValue }];
          });
          setCreatedOptionValues((prev) => {
            const next = new Set(prev);
            next.add(normalizeOptionValue(resolvedValue));
            return next;
          });
        } else {
          resolvedValue = newValue.value;
        }

        setInputValue(resolvedValue);
        setCurrentSelectionValue(resolvedValue);
        const syntheticEvent = createSyntheticEvent(name ?? "", resolvedValue);
        onChange(syntheticEvent);
      }}
      sx={[defaultSx, sx as any]}
      slotProps={{
        paper: {
          elevation: 4,
          sx: {
            border: "1px solid rgba(0, 0, 0, 0.18)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.14)",
            mt: "2px",
          },
        },
      }}
      ListboxProps={{
        style: {
          fontSize,
          fontFamily,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          helperText={inputHelp}
          required={required}
          name={name}
          variant="outlined"
          size={size}
          inputProps={{
            ...params.inputProps,
            placeholder,
            style: {
              ...(params.inputProps.style ?? {}),
              fontSize,
              fontFamily,
              height: 30,
              boxSizing: "border-box",
              padding: "0 6px",
            },
          }}
        />
      )}
    />
  );
}

export default CreatableSingleDropDown;
